// Agent loops panel (self-prompting iterative runs).
//
// Feed-first UX: the loops list sits on top; selecting a loop (or starting
// one) shows a fixed-height live journal feed below it that auto-refreshes
// while the loop runs — no navigation away from the tab. Reuses task-* CSS
// for cards; feed has its own loop-feed styles.
import { getHeaders } from './api.js';
import { isContainerMode } from './state.js';
import { escapeHtml } from './html-escape.js';
import { showToast } from './toast.js';

let currentAgent = null;
let loopsList = [];
let editingLoopId = null;
let editorOpen = false; // guards the poll: never re-render over an open editor
let selectedLoopId = null; // loop whose feed is shown
let pollTimer = null;

const STATUS_META = {
    draft:          { cls: 'paused', label: 'Draft' },
    running:        { cls: 'active', label: 'Running' },
    paused:         { cls: 'paused', label: 'Paused' },
    awaiting_human: { cls: 'paused', label: 'Needs Review' },
    completed:      { cls: 'done',   label: 'Completed' },
    failed:         { cls: 'paused', label: 'Failed' },
    killed:         { cls: 'paused', label: 'Killed' },
};

const VERDICT_LABEL = {
    done: '✓ done',
    continue: '→ continue',
    stuck: '↻ stuck',
    needs_human: '⚠ needs human',
};

function loopsTabVisible() {
    const tab = document.getElementById('agent-tab-loops');
    return tab && !tab.classList.contains('hidden');
}

function anyLoopRunning() {
    return loopsList.some(l => l.status === 'running');
}

function stopPolling() {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

function maybeStartPolling() {
    stopPolling();
    if (!anyLoopRunning()) return;
    pollTimer = setInterval(async () => {
        // Self-heal: stop when the tab is closed or nothing is running
        if (!loopsTabVisible() || !currentAgent) { stopPolling(); return; }
        if (editorOpen) return; // never repaint over an open editor
        await loadLoops(currentAgent, { silent: true });
    }, 4000);
}

export function stopLoopsPolling() {
    stopPolling();
}

export async function loadLoops(agentName, opts = {}) {
    if (agentName && agentName !== currentAgent) {
        currentAgent = agentName;
        selectedLoopId = null; // agent switch resets selection
    }
    if (!currentAgent) return;
    const headers = getHeaders();
    try {
        const resp = await fetch(`/agents/${encodeURIComponent(currentAgent)}/loops`, { headers });
        if (!resp.ok) throw new Error('Failed to load loops');
        const data = await resp.json();
        loopsList = data.loops || [];
        // Default selection: the running loop, else most recent non-draft
        if (!selectedLoopId) {
            const running = loopsList.find(l => l.status === 'running');
            if (running) selectedLoopId = running.id;
        }
        if (selectedLoopId && !loopsList.some(l => l.id === selectedLoopId)) {
            selectedLoopId = null;
        }
        if (!editorOpen) {
            renderLoopsList();
            await renderFeed();
        }
        maybeStartPolling();
    } catch (error) {
        console.error('Failed to load loops:', error);
        const list = document.getElementById('agent-loops-list');
        if (list) list.innerHTML = '<p class="tasks-empty">Failed to load loops</p>';
    }
}

function formatTokens(n) {
    if (!n) return '0';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
}

function renderLoopsList() {
    const list = document.getElementById('agent-loops-list');
    if (!list) return;
    editorOpen = false; // list view implies no editor (self-healing)

    document.getElementById('loop-editor')?.classList.add('hidden');
    document.getElementById('loops-toolbar')?.classList.remove('hidden');

    if (loopsList.length === 0) {
        list.innerHTML = '<p class="tasks-empty">No loops yet. A loop gives this agent a long-running mission it works on iteratively, checking its own progress between steps.</p>';
        document.getElementById('loop-feed')?.classList.add('hidden');
        return;
    }

    list.innerHTML = loopsList.map(loop => {
        const meta = STATUS_META[loop.status] || STATUS_META.draft;
        const canStart = ['draft', 'failed'].includes(loop.status);
        const canResume = ['paused', 'awaiting_human'].includes(loop.status);
        const isRunning = loop.status === 'running';
        const missionPreview = loop.mission.length > 120 ? loop.mission.slice(0, 120) + '...' : loop.mission;
        const selected = loop.id === selectedLoopId;

        return `
            <div class="task-card loop-card ${selected ? 'loop-card-selected' : ''}" data-loop-id="${loop.id}">
                <div class="task-card-header">
                    <div class="task-card-status ${meta.cls}"></div>
                    <div class="task-card-info">
                        <span class="task-card-name">${escapeHtml(loop.name)}</span>
                        <span class="task-card-schedule">Iteration ${loop.iteration}${loop.max_loop_iterations ? ` / ${loop.max_loop_iterations}` : ''} · ${formatTokens(loop.tokens_spent)} tok</span>
                    </div>
                    <span class="task-status-badge ${meta.cls}">${meta.label}</span>
                </div>
                <div class="task-card-prompt">${escapeHtml(missionPreview)}</div>
                <div class="task-card-actions">
                    ${canStart || canResume ? `
                    <button class="task-action-btn task-btn-paused" data-action="${canStart ? 'start' : 'resume'}" title="${canStart ? 'Start' : 'Resume'}">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    </button>` : ''}
                    ${isRunning ? `
                    <button class="task-action-btn task-btn-active" data-action="pause" title="Pause (takes effect after the current step)">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                    </button>
                    <button class="task-action-btn danger" data-action="kill" title="Kill">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="5" width="14" height="14" rx="2"/></svg>
                    </button>` : `
                    <button class="task-action-btn" data-action="edit" title="Edit">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button class="task-action-btn danger" data-action="delete" title="Delete">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                    </button>`}
                </div>
            </div>
        `;
    }).join('');

    // Card click selects (shows feed); action buttons act
    list.querySelectorAll('.loop-card').forEach(card => {
        const loopId = parseInt(card.dataset.loopId);
        card.addEventListener('click', async (e) => {
            if (e.target.closest('.task-action-btn')) return;
            selectedLoopId = selectedLoopId === loopId ? null : loopId;
            renderLoopsList();
            await renderFeed();
        });
    });
    list.querySelectorAll('.task-action-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const loopId = parseInt(btn.closest('.task-card').dataset.loopId);
            const action = btn.dataset.action;
            if (action === 'edit') showLoopEditor(loopId);
            else if (action === 'start' || action === 'resume') controlLoop(loopId, action);
            else if (action === 'pause') controlLoop(loopId, 'pause');
            else if (action === 'kill') killLoop(loopId);
            else if (action === 'delete') deleteLoop(loopId);
        });
    });
}

async function controlLoop(loopId, verb) {
    const headers = getHeaders();
    try {
        const resp = await fetch(`/agents/${encodeURIComponent(currentAgent)}/loops/${loopId}/${verb}`, {
            method: 'POST', headers
        });
        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw new Error(err.detail || `Failed to ${verb} loop`);
        }
        showToast(`Loop ${verb === 'pause' ? 'pause requested' : verb + (verb.endsWith('e') ? 'd' : 'ed')}`, 'success');
        // Starting/resuming focuses the feed on that loop immediately
        if (verb === 'start' || verb === 'resume') selectedLoopId = loopId;
        await loadLoops(currentAgent);
    } catch (error) {
        showToast(error.message, 'error');
    }
}

async function killLoop(loopId) {
    const loop = loopsList.find(l => l.id === loopId);
    if (!loop) return;
    if (!confirm(`Kill loop "${loop.name}"? It cannot be restarted afterwards.`)) return;
    await controlLoop(loopId, 'kill');
}

async function deleteLoop(loopId) {
    const loop = loopsList.find(l => l.id === loopId);
    if (!loop) return;
    if (!confirm(`Delete loop "${loop.name}"? This also deletes its journal.`)) return;
    const headers = getHeaders();
    try {
        const resp = await fetch(`/agents/${encodeURIComponent(currentAgent)}/loops/${loopId}`, {
            method: 'DELETE', headers
        });
        if (!resp.ok) throw new Error('Failed to delete loop');
        if (selectedLoopId === loopId) selectedLoopId = null;
        await loadLoops(currentAgent);
    } catch (error) {
        showToast(error.message, 'error');
    }
}

// ── Live feed ──────────────────────────────────────────────────────────

async function renderFeed() {
    const feed = document.getElementById('loop-feed');
    if (!feed) return;
    if (!selectedLoopId) {
        feed.classList.add('hidden');
        return;
    }
    const headers = getHeaders();
    try {
        const resp = await fetch(
            `/agents/${encodeURIComponent(currentAgent)}/loops/${selectedLoopId}?limit=50`, { headers }
        );
        if (!resp.ok) throw new Error('Failed to load loop');
        const data = await resp.json();
        const loop = data.loop;
        const iterations = (data.iterations || []).slice().reverse(); // oldest first
        const meta = STATUS_META[loop.status] || STATUS_META.draft;

        feed.classList.remove('hidden');
        const titleEl = document.getElementById('loop-feed-title');
        if (titleEl) titleEl.textContent = loop.name;
        const statusEl = document.getElementById('loop-feed-status');
        if (statusEl) {
            statusEl.innerHTML = `
                <span class="task-status-badge ${meta.cls}">${meta.label}</span>
                <span>Iteration ${loop.iteration}${loop.max_loop_iterations ? ` / ${loop.max_loop_iterations}` : ''}</span>
                <span>${formatTokens(loop.tokens_spent)} tok</span>
                ${loop.last_error ? `<span class="loop-feed-error" title="${escapeHtml(loop.last_error)}">${escapeHtml(loop.last_error.length > 70 ? loop.last_error.slice(0, 70) + '...' : loop.last_error)}</span>` : ''}
            `;
        }
        const planEl = document.getElementById('loop-feed-plan');
        if (planEl) {
            planEl.innerHTML = loop.plan
                ? `<details><summary>Current plan</summary><pre>${escapeHtml(loop.plan)}</pre></details>`
                : '';
        }

        const listEl = document.getElementById('loop-feed-list');
        if (!listEl) return;

        // Preserve expansion + scroll position across refreshes
        const expanded = new Set(
            [...listEl.querySelectorAll('.task-run-item.expanded')].map(el => el.dataset.iter)
        );
        const nearBottom = listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight < 60;
        const prevScroll = listEl.scrollTop;

        if (iterations.length === 0) {
            listEl.innerHTML = `<p class="tasks-empty">${loop.status === 'running' ? 'Iteration 1 in progress…' : 'No iterations yet — press ▶ to start'}</p>`;
            return;
        }

        listEl.innerHTML = iterations.map(it => {
            const running = it.status === 'running';
            const verdict = it.verdict
                ? (VERDICT_LABEL[it.verdict] || it.verdict)
                : (running ? 'working…' : it.status);
            const verifyBadge = it.verify_passed === null || it.verify_passed === undefined
                ? ''
                : it.verify_passed
                    ? '<span class="run-status success" title="Verify passed">&#10003;</span>'
                    : '<span class="run-status error" title="Verify failed">&#10007;</span>';
            const entry = it.journal_entry || it.error || '';
            const entryPreview = entry.length > 160 ? entry.slice(0, 160) + '...' : entry;
            return `
                <div class="task-run-item ${expanded.has(String(it.iteration)) ? 'expanded' : ''} ${running ? 'loop-iter-running' : ''}" data-iter="${it.iteration}">
                    <div class="task-run-summary" onclick="this.parentElement.classList.toggle('expanded')">
                        <span class="run-time">#${it.iteration}</span>
                        ${verifyBadge}
                        <span class="loop-iter-verdict">${escapeHtml(verdict)}</span>
                        <span class="loop-iter-entry">${escapeHtml(entryPreview)}</span>
                        ${it.tokens_used ? `<span class="run-tokens">${formatTokens(it.tokens_used)}</span>` : ''}
                        <svg class="run-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                    <div class="task-run-detail">
                        ${entry ? `<div class="run-section"><strong>Journal:</strong><pre>${escapeHtml(entry)}</pre></div>` : ''}
                        ${it.next_instruction ? `<div class="run-section"><strong>Next instruction:</strong><pre>${escapeHtml(it.next_instruction)}</pre></div>` : ''}
                        ${it.worker_response ? `<div class="run-section"><strong>Worker output:</strong><pre>${escapeHtml(it.worker_response.length > 3000 ? it.worker_response.slice(0, 3000) + '\n…(truncated)' : it.worker_response)}</pre></div>` : ''}
                        ${it.verify_output ? `<div class="run-section"><strong>Verify output:</strong><pre>${escapeHtml(it.verify_output)}</pre></div>` : ''}
                        ${it.error ? `<div class="run-section run-error"><strong>Error:</strong><pre>${escapeHtml(it.error)}</pre></div>` : ''}
                    </div>
                </div>
            `;
        }).join('');

        // Auto-follow the feed unless the user scrolled up to read history
        listEl.scrollTop = nearBottom ? listEl.scrollHeight : prevScroll;
    } catch (error) {
        console.error('Failed to render loop feed:', error);
    }
}

// ── Editor ─────────────────────────────────────────────────────────────

export function showLoopEditor(loopId) {
    editingLoopId = loopId;
    editorOpen = true;
    const editor = document.getElementById('loop-editor');
    if (!editor) return;

    document.getElementById('loops-toolbar')?.classList.add('hidden');
    document.getElementById('agent-loops-list').innerHTML = '';
    document.getElementById('loop-feed')?.classList.add('hidden');
    editor.classList.remove('hidden');
    document.getElementById('loop-editor-title').textContent = loopId ? 'Edit Loop' : 'New Loop';
    document.getElementById('loop-editor-error').textContent = '';

    const setVal = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
    const loop = loopId ? loopsList.find(l => l.id === loopId) : null;

    setVal('loop-name', loop?.name || '');
    setVal('loop-mission', loop?.mission || '');
    setVal('loop-plan', loop?.plan || '');
    setVal('loop-verify-command', loop?.verify_command || '');
    setVal('loop-working-dir', loop?.working_dir || '');
    // The verify command runs on the server, so a workspace sees it but only
    // an administrator sets it (the API refuses a change from a container).
    for (const id of ['loop-verify-command', 'loop-working-dir']) {
        const el = document.getElementById(id);
        if (!el) continue;
        el.readOnly = isContainerMode();
        el.title = isContainerMode() ? 'Set by an administrator' : '';
    }
    setVal('loop-max-iterations', loop?.max_loop_iterations ?? 25);
    setVal('loop-max-tokens', loop?.max_total_tokens ?? 0);
    setVal('loop-max-wall-minutes', loop ? Math.round((loop.max_wall_seconds || 0) / 60) : 0);
    setVal('loop-pause-every-n', loop?.pause_every_n ?? 0);
    setVal('loop-done-threshold', loop?.done_threshold ?? 2);
    setVal('loop-model', loop?.model || '');
    setVal('loop-check-model', loop?.check_model || '');

    for (const kind of ['model', 'check-model']) {
        const display = document.getElementById(`loop-${kind}-display`);
        const raw = kind === 'model' ? loop?.model : loop?.check_model;
        if (display) { display.textContent = raw || ''; display.title = raw || ''; }
    }
}

export function hideLoopEditor() {
    document.getElementById('loop-editor')?.classList.add('hidden');
    editingLoopId = null;
    editorOpen = false;
    renderLoopsList();
    renderFeed();
}

export async function handleSaveLoop() {
    const errorEl = document.getElementById('loop-editor-error');
    const getVal = (id) => document.getElementById(id)?.value ?? '';
    const name = getVal('loop-name').trim();
    const mission = getVal('loop-mission').trim();
    if (!name) { errorEl.textContent = 'Name is required'; return; }
    if (!mission) { errorEl.textContent = 'Mission is required'; return; }

    const body = {
        name,
        mission,
        plan: getVal('loop-plan'),
        verify_command: getVal('loop-verify-command').trim() || null,
        working_dir: getVal('loop-working-dir').trim() || null,
        model: getVal('loop-model') || null,
        check_model: getVal('loop-check-model') || null,
        max_loop_iterations: parseInt(getVal('loop-max-iterations')) || 25,
        max_total_tokens: parseInt(getVal('loop-max-tokens')) || 0,
        max_wall_seconds: (parseInt(getVal('loop-max-wall-minutes')) || 0) * 60,
        pause_every_n: parseInt(getVal('loop-pause-every-n')) || 0,
        done_threshold: parseInt(getVal('loop-done-threshold')) || 1,
    };

    const headers = getHeaders();
    try {
        const url = editingLoopId
            ? `/agents/${encodeURIComponent(currentAgent)}/loops/${editingLoopId}`
            : `/agents/${encodeURIComponent(currentAgent)}/loops`;
        const resp = await fetch(url, {
            method: editingLoopId ? 'PUT' : 'POST',
            headers,
            body: JSON.stringify(body),
        });
        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw new Error(err.detail || 'Failed to save loop');
        }
        const saved = await resp.json().catch(() => ({}));
        if (saved.loop?.id) selectedLoopId = saved.loop.id;
        editingLoopId = null;
        editorOpen = false;
        document.getElementById('loop-editor')?.classList.add('hidden');
        await loadLoops(currentAgent);
    } catch (error) {
        errorEl.textContent = error.message;
    }
}
